import { StopwatchOutline18Icon } from "@vygruppen/spor-icon-react";
import { StopIcon } from "features/CenterContent/shared/StopIcon";
import { FillRate } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/FillRate";
import { Train } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/Train";
import { TrainTime } from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/TrainTime";
import {
  calculateMinuteDelay,
  getTrainLineColor,
  getTrainSemantic,
  isCancelled,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/TrainStop/utils";
import {
  Count,
  Fillrate,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainRoute/types";
import { TrainRouteStop } from "features/CenterContent/VehicleDetails/TrainDetails/useTrainRoute";
import { TFunction } from "i18next";
import { MutableRefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Semantics } from "shared/components/drops/DropsBadge";
import {
  Box,
  Flex,
  HStack,
  TableCell,
  TableRow,
  Text,
  Tooltip,
  VStack,
} from "shared/components/Spor";
import { useThemeOrSemantic } from "shared/theme/themeSemantic";
import styled from "styled-components";

const combineCounts = (counts: Count[]): Count =>
  counts.reduce((prev, current) => ({
    passengerCountStatus:
      current.passengerCountStatus !== "ACTUAL"
        ? current.passengerCountStatus
        : prev.passengerCountStatus,
    vehicleSetId: `${prev.vehicleSetId}, ${current.vehicleSetId}`,
    passengersIn: prev.passengersIn + current.passengersIn,
    passengersOut: prev.passengersOut + current.passengersOut,
    passengerCount: prev.passengerCount + current.passengerCount,
  }));

const gapPx = 1; // Adjustable gap between icon and line
const iconHeightPx = 16;
export const TdPaddingTopPx = 12;
export const TdPaddingBottomPx = 20;
export const TdPaddingSidePx = 6;
const Line = styled(Box)<{ $iconHeightPx: number }>`
  position: absolute;
  margin-top: ${({ $iconHeightPx }) => $iconHeightPx + gapPx}px;
  height: calc(
    100% +
      ${({ $iconHeightPx }) =>
        TdPaddingTopPx + TdPaddingBottomPx - 2 * gapPx - $iconHeightPx}px
  );
  width: 1px;
`;

type TrainStopProps = {
  prevSemantics: MutableRefObject<Semantics | undefined>;
  stop: TrainRouteStop;
  isLastStop: boolean;
  isCurrentStop: boolean;
  isVisited: boolean;
  isNoPassing: boolean;
  fillrate: Fillrate | null;
};

export const TrainStopRow = ({
  prevSemantics,
  stop,
  isLastStop,
  isCurrentStop,
  isVisited,
  isNoPassing,
  fillrate,
}: TrainStopProps) => {
  const arrivalDelay =
    stop.arrivalDelay ??
    calculateMinuteDelay(stop.scheduledArrivalTime, stop.estimatedArrivalTime);
  const departureDelay =
    stop.departureDelay ??
    calculateMinuteDelay(
      stop.scheduledDepartureTime,
      stop.estimatedDepartureTime,
    );
  const isNoPassingAndNoEstimatedTimes =
    isNoPassing && !stop.estimatedArrivalTime && !stop.estimatedDepartureTime;

  const stopIsCancelled = isCancelled(stop);
  const semantic = isNoPassingAndNoEstimatedTimes
    ? (prevSemantics.current ?? "success") // If there is no previous semantic, then it is the first stop and therefore have no known delay.
    : getTrainSemantic({
        delay: departureDelay || arrivalDelay || 0,
        isVisited,
        isCancelled: stopIsCancelled,
      });
  // eslint-disable-next-line no-param-reassign -- Is a ref so we can reassign it
  prevSemantics.current = semantic;
  const theme = useThemeOrSemantic(semantic);
  const { t } = useTranslation();

  const lineColor = getTrainLineColor({
    isVisited,
    isCancelled: stopIsCancelled,
    theme,
  });
  // Same as line color, but colorTextMain instead of colorTextDisabled for upcomming stops
  const stopColor = isVisited ? lineColor : theme.colorTextMain;

  const { bgColor, textMainColor, textSecondaryColor, borderColor } = {
    bgColor: theme.colorSemanticFill ?? theme.colorBackgroundSecondary,
    textMainColor: theme.colorSemanticTextSecondary ?? lineColor,
    textSecondaryColor: theme.colorSemanticTextSecondary ?? lineColor,
    borderColor: theme.colorSemanticOutline ?? theme.colorOutlineMain,
  };

  const visitedTextColor = isCurrentStop ? textMainColor : textSecondaryColor;
  const unvisitedTextColor = stop.justPassingThrough
    ? theme.colorTextDisabled
    : theme.colorTextMain;
  const textColor = isVisited ? visitedTextColor : unvisitedTextColor;

  const stopCount = fillrate?.stops.find((s) => s.stopId === stop.stopId);

  const hideLine = isLastStop;

  const getTooltipLabel = (translate: TFunction<"translation", undefined>) => {
    if (stopIsCancelled) return translate("Innstilt på stopp");
    if (stop.justPassingThrough) return translate("Passerer gjennom");
    return translate("Stopper");
  };

  const componentRef = useRef<HTMLTableRowElement>(null);

  useEffect(() => {
    if (!isCurrentStop) return;
    componentRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  return (
    <TableRow
      ref={componentRef}
      backgroundColor={isCurrentStop ? bgColor : undefined}
      // Must use outline (instead of border) as the border affects the width of the table which creates a margin
      outlineOffset="-1px"
      outline={`1px solid ${isCurrentStop ? borderColor : undefined}`}
    >
      <TableCell>
        <VStack height="100%" position="relative" mt="6px">
          {isCurrentStop ? (
            <Train color={stopColor} />
          ) : (
            !stop.justPassingThrough && (
              <Tooltip label={getTooltipLabel(t)}>
                <Flex /* Necessary for tooltip. ShouldWrapChildren did not work. */
                >
                  <StopIcon
                    justPassingThrough={stop.justPassingThrough}
                    isCancelled={stopIsCancelled}
                    color={stopColor}
                  />
                </Flex>
              </Tooltip>
            )
          )}
          {!hideLine && (
            <Line
              backgroundColor={lineColor}
              $iconHeightPx={stop.justPassingThrough ? -3 : iconHeightPx} // -3 instead of 0 in order to close the gap
            />
          )}
        </VStack>
      </TableCell>
      <TableCell>
        <VStack alignItems="start" height="100%" gap={0}>
          <HStack>
            <Text
              variant="xs"
              color={textColor}
              fontWeight={600}
              textDecoration={stopIsCancelled ? "line-through" : "none"}
            >
              {stop.name}
            </Text>
            {isNoPassing && (
              <Tooltip
                hasArrow
                placement="right"
                label={t("Ingen passeringsmeldinger for dette stoppet.")}
              >
                <span style={{ cursor: "pointer" }}>
                  <StopwatchOutline18Icon color={lineColor} />
                </span>
              </Tooltip>
            )}
          </HStack>
          <Box
            // This makes it so that fillRate can overflow to the next column, which is necessary for small widths
            w="max-content"
          >
            {stopCount && stopCount.counts.length > 0 && (
              <FillRate
                count={combineCounts(stopCount.counts)}
                isVisited={isVisited}
              />
            )}
          </Box>
        </VStack>
      </TableCell>
      <TableCell>
        <VStack height="100%" alignItems="start">
          {stop.track && (
            <Text
              variant="xs"
              color={
                isVisited ? theme.colorTextSecondary : theme.colorTextDisabled
              }
            >
              {stop.track}
            </Text>
          )}
        </VStack>
      </TableCell>
      <TableCell colSpan={2} padding="0px !important">
        <HStack gap="0" pt={`${TdPaddingTopPx}px`}>
          <Box flex={1} px={`${TdPaddingSidePx}px`}>
            <TrainTime
              scheduledTime={stop.scheduledArrivalTime}
              actualTime={stop.actualArrivalTime}
              estimatedTime={stop.estimatedArrivalTime}
              delay={arrivalDelay ?? 0}
            />
          </Box>
          <Box flex={1} px={`${TdPaddingSidePx}px`}>
            <TrainTime
              scheduledTime={stop.scheduledDepartureTime}
              actualTime={stop.actualDepartureTime}
              estimatedTime={stop.estimatedDepartureTime}
              delay={departureDelay ?? 0}
            />
          </Box>
        </HStack>
        <Flex pb={`${TdPaddingBottomPx}px`} px={`${TdPaddingSidePx}px`} />
      </TableCell>
    </TableRow>
  );
};
